import { useState } from 'react'
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react'
import { useGamesByDate } from '../../hooks/useLiveScores'
import SportSelector from '../ui/SportSelector'
import GameCard from '../scores/GameCard'
import LoadingSpinner from '../ui/LoadingSpinner'
import type { Sport } from '../../types'

export default function GameSchedule() {
  const [selectedSport, setSelectedSport] = useState<Sport>('NFL')
  const [selectedDate, setSelectedDate] = useState(getTodayString())

  const { data: games, isLoading, error } = useGamesByDate(selectedSport, selectedDate)

  const handlePreviousDay = () => {
    const date = new Date(selectedDate)
    date.setDate(date.getDate() - 1)
    setSelectedDate(formatDate(date))
  }

  const handleNextDay = () => {
    const date = new Date(selectedDate)
    date.setDate(date.getDate() + 1)
    setSelectedDate(formatDate(date))
  }

  const handleToday = () => {
    setSelectedDate(getTodayString())
  }

  const isToday = selectedDate === getTodayString()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Game Schedule</h1>
        <p className="text-sm text-gray-500">
          Browse upcoming and past games by sport and date
        </p>
      </div>

      {/* Sport filter */}
      <SportSelector
        selected={selectedSport}
        onChange={(sport) => {
          if (sport !== 'ALL') setSelectedSport(sport)
        }}
        showAll={false}
      />

      {/* Date picker */}
      <div className="flex items-center justify-between card p-3">
        <button
          onClick={handlePreviousDay}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <CalendarIcon className="w-5 h-5 text-gray-400" />
            <span className="font-medium text-gray-900">
              {formatDisplayDate(selectedDate)}
            </span>
          </div>
          {!isToday && (
            <button
              onClick={handleToday}
              className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Today
            </button>
          )}
        </div>

        <button
          onClick={handleNextDay}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>

      {/* Games grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner size="lg" />
        </div>
      ) : error ? (
        <div className="card p-12 text-center">
          <p className="text-gray-500">Unable to load games. Please try again.</p>
        </div>
      ) : games && games.length > 0 ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {games.map((game) => (
            <GameCard key={game.id} game={game} />
          ))}
        </div>
      ) : (
        <EmptyState sport={selectedSport} date={selectedDate} />
      )}
    </div>
  )
}

interface EmptyStateProps {
  sport: Sport
  date: string
}

function EmptyState({ sport, date }: EmptyStateProps) {
  return (
    <div className="card p-12 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <CalendarIcon className="w-8 h-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No Games Scheduled</h3>
      <p className="text-gray-500">
        No {sport} games are scheduled for {formatDisplayDate(date)}.
      </p>
    </div>
  )
}

function getTodayString(): string {
  return formatDate(new Date())
}

function formatDate(date: Date): string {
  return date.toISOString().split('T')[0]
}

function formatDisplayDate(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00')
  const today = new Date()
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (formatDate(date) === formatDate(today)) return 'Today'
  if (formatDate(date) === formatDate(tomorrow)) return 'Tomorrow'
  if (formatDate(date) === formatDate(yesterday)) return 'Yesterday'

  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}
